import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  MdAttachMoney,
  MdBarChart,
  MdCheckCircle,
  MdDownload,
  MdPeople,
  MdPayment,
  MdSchool,
  MdVisibility,
} from 'react-icons/md';

import Card from 'components/card/Card';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import { getCurrentUser } from 'services/authService';
import reportsService from 'services/reportsService';
import studentsService from 'services/studentsService';
import tuitionService from 'services/tuitionService';
import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPaymentMethod,
  formatStatus,
} from 'utils/format';

const fetchLimit = 8;

function getPaymentStudentName(payment) {
  return payment.student_name || payment.student?.name || payment.tuition_student_name || 'Unknown student';
}

function getPaymentTuitionLabel(payment) {
  return payment.tuition_type || payment.type || payment.tuition_description || payment.description || 'Tuition';
}

function StatusBadge({ status }) {
  const colorScheme =
    status === 'validated' || status === 'paid'
      ? 'green'
      : status === 'pending'
        ? 'yellow'
        : status === 'rejected' || status === 'overdue'
          ? 'red'
          : 'gray';

  return (
    <Badge colorScheme={colorScheme} fontSize='xs' fontWeight='700' px='8px' py='2px'>
      {formatStatus(status)}
    </Badge>
  );
}

function SectionHeader({ title, action }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  return (
    <Flex justify='space-between' align={{ base: 'flex-start', md: 'center' }} gap='12px' mb='18px'>
      <Text color={textColor} fontSize='lg' fontWeight='700'>
        {title}
      </Text>
      {action}
    </Flex>
  );
}

function CenterAdminDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const rowHoverBg = useColorModeValue('secondaryGray.50', 'whiteAlpha.50');

  const currentUser = getCurrentUser();
  const centerName = currentUser?.center || '';

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [students, setStudents] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [studentTotal, setStudentTotal] = useState(0);
  const [tuitionTotal, setTuitionTotal] = useState(0);
  const [paymentTotals, setPaymentTotals] = useState({
    pendingCount: 0,
    pendingAmount: 0,
    validatedCount: 0,
    validatedAmount: 0,
  });
  const [validatingId, setValidatingId] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const loadDashboard = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    const [studentsResult, tuitionsResult, pendingResult, validatedResult, recentResult] =
      await Promise.all([
        studentsService.getStudents({ limit: fetchLimit }),
        tuitionService.getTuitions({ limit: fetchLimit }),
        tuitionService.getPayments({ status: 'pending', limit: fetchLimit }),
        tuitionService.getPayments({ status: 'validated', limit: 100 }),
        tuitionService.getPayments({ limit: fetchLimit }),
      ]);

    const failed = [studentsResult, tuitionsResult, pendingResult, validatedResult, recentResult].find(
      (result) => !result.success,
    );

    if (failed) {
      setError(failed.error || 'Failed to load center dashboard');
    } else {
      const pendingRows = pendingResult.data || [];
      const validatedRows = validatedResult.data || [];

      setStudents(studentsResult.data || []);
      setPendingPayments(pendingRows);
      setRecentPayments(recentResult.data || []);
      setStudentTotal(studentsResult.pagination?.total || studentsResult.data?.length || 0);
      setTuitionTotal(tuitionsResult.pagination?.total || tuitionsResult.data?.length || 0);
      setPaymentTotals({
        pendingCount: pendingResult.pagination?.total || pendingRows.length,
        pendingAmount: pendingRows.reduce((sum, payment) => sum + Number(payment.amount || 0), 0),
        validatedCount: validatedResult.pagination?.total || validatedRows.length,
        validatedAmount: validatedRows.reduce((sum, payment) => sum + Number(payment.amount || 0), 0),
      });
    }

    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const activeFields = useMemo(() => {
    const fields = new Set(students.map((student) => student.field).filter(Boolean));
    return fields.size;
  }, [students]);

  const handleValidatePayment = async (payment) => {
    setValidatingId(payment.id);
    const result = await tuitionService.validatePayment(payment.id, {
      status: 'validated',
      notes: `Validated from ${centerName || 'center'} dashboard`,
    });

    if (result.success) {
      toast({
        title: 'Payment validated',
        description: `${formatCurrency(payment.amount)} for ${getPaymentStudentName(payment)} is now validated.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      await loadDashboard(true);
    } else {
      toast({
        title: 'Validation failed',
        description: result.error || 'Failed to validate payment.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }

    setValidatingId(null);
  };

  const handleDownload = async (type) => {
    setDownloading(type);
    const result =
      type === 'class-list'
        ? await reportsService.downloadClassListPdf({ limit: 1000 })
        : await reportsService.downloadTuitionPaymentsPdf({ limit: 1000 });

    toast({
      title: result.success ? 'Download started' : 'Download failed',
      description: result.success
        ? result.filename || 'Your report is being downloaded.'
        : result.error || 'Could not download the report.',
      status: result.success ? 'success' : 'error',
      duration: result.success ? 4000 : 6000,
      isClosable: true,
    });
    setDownloading(null);
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Flex justify='center' align='center' minH='420px'>
          <Spinner size='xl' color='brand.500' />
        </Flex>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify='space-between' align={{ base: 'flex-start', md: 'center' }} gap='16px' mb='22px'>
        <Box>
          <Text color={textColor} fontSize='2xl' fontWeight='700'>
            Center Admin Dashboard
          </Text>
          <Text color={mutedText} fontSize='sm' mt='4px'>
            {centerName ? `${centerName} center` : 'Your center'} students, payments, tuition, and reports.
          </Text>
        </Box>
        <Button
          variant='outline'
          colorScheme='brand'
          isLoading={refreshing}
          loadingText='Refreshing'
          onClick={() => loadDashboard(true)}
        >
          Refresh
        </Button>
      </Flex>

      {error && (
        <Alert status='error' borderRadius='12px' mb='20px'>
          <AlertIcon />
          {error}
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap='20px' mb='20px'>
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdPeople} color={brandColor} />}
            />
          }
          name='Center Students'
          value={formatNumber(studentTotal)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdSchool} color={brandColor} />}
            />
          }
          name='Open Tuitions'
          value={formatNumber(tuitionTotal)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdPayment} color='orange.400' />}
            />
          }
          name='Pending Payments'
          value={formatNumber(paymentTotals.pendingCount)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdAttachMoney} color='green.500' />}
            />
          }
          name='Validated Revenue'
          value={formatCurrency(paymentTotals.validatedAmount)}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 3 }} gap='20px' mb='20px' alignItems='stretch'>
        <Card p='22px'>
          <SectionHeader
            title='Quick Actions'
            action={
              <Button size='sm' colorScheme='brand' onClick={() => navigate('/admin/students')}>
                Manage students
              </Button>
            }
          />
          <SimpleGrid columns={1} gap='12px'>
            <Button justifyContent='flex-start' leftIcon={<MdPeople />} onClick={() => navigate('/admin/students')}>
              Review student records
            </Button>
            <Button justifyContent='flex-start' leftIcon={<MdPayment />} onClick={() => navigate('/admin/tuition')}>
              Manage tuition and payments
            </Button>
            <Button justifyContent='flex-start' leftIcon={<MdBarChart />} onClick={() => navigate('/admin/student-reports')}>
              Generate student reports
            </Button>
          </SimpleGrid>
        </Card>

        <Card p='22px'>
          <SectionHeader title='Report Downloads' />
          <SimpleGrid columns={1} gap='12px'>
            <Button
              justifyContent='space-between'
              rightIcon={<MdDownload />}
              isLoading={downloading === 'class-list'}
              onClick={() => handleDownload('class-list')}
            >
              Class list PDF
            </Button>
            <Button
              justifyContent='space-between'
              rightIcon={<MdDownload />}
              isLoading={downloading === 'tuition'}
              onClick={() => handleDownload('tuition')}
            >
              Tuition payments PDF
            </Button>
          </SimpleGrid>
        </Card>

        <Card p='22px'>
          <SectionHeader title='Center Snapshot' />
          <Flex direction='column' gap='14px'>
            <Flex justify='space-between'>
              <Text color={mutedText} fontSize='sm'>Active fields</Text>
              <Text color={textColor} fontWeight='700'>{formatNumber(activeFields)}</Text>
            </Flex>
            <Flex justify='space-between'>
              <Text color={mutedText} fontSize='sm'>Pending amount</Text>
              <Text color={textColor} fontWeight='700'>{formatCurrency(paymentTotals.pendingAmount)}</Text>
            </Flex>
            <Flex justify='space-between'>
              <Text color={mutedText} fontSize='sm'>Validated payments</Text>
              <Text color={textColor} fontWeight='700'>{formatNumber(paymentTotals.validatedCount)}</Text>
            </Flex>
          </Flex>
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px' mb='20px'>
        <Card p='22px' overflowX='auto'>
          <SectionHeader
            title='Pending Payment Validation'
            action={
              <Button size='sm' variant='outline' onClick={() => navigate('/admin/tuition')}>
                View all
              </Button>
            }
          />
          <Table variant='simple' size='sm'>
            <Thead>
              <Tr>
                <Th borderColor={borderColor}>Student</Th>
                <Th borderColor={borderColor}>Amount</Th>
                <Th borderColor={borderColor}>Method</Th>
                <Th borderColor={borderColor}>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pendingPayments.length === 0 ? (
                <Tr>
                  <Td colSpan={4} borderColor='transparent'>
                    <Text color={mutedText} fontSize='sm'>No pending payments.</Text>
                  </Td>
                </Tr>
              ) : (
                pendingPayments.map((payment) => (
                  <Tr key={payment.id} _hover={{ bg: rowHoverBg }}>
                    <Td borderColor='transparent'>
                      <Text color={textColor} fontSize='sm' fontWeight='700'>{getPaymentStudentName(payment)}</Text>
                      <Text color={mutedText} fontSize='xs'>{getPaymentTuitionLabel(payment)}</Text>
                    </Td>
                    <Td borderColor='transparent'>
                      <Text color={textColor} fontSize='sm' fontWeight='700'>{formatCurrency(payment.amount)}</Text>
                    </Td>
                    <Td borderColor='transparent'>
                      <Text color={textColor} fontSize='sm'>{formatPaymentMethod(payment.payment_method)}</Text>
                    </Td>
                    <Td borderColor='transparent'>
                      <HStack spacing='6px'>
                        <IconButton
                          aria-label='View payment'
                          icon={<MdVisibility />}
                          size='sm'
                          variant='ghost'
                          onClick={() => navigate(`/admin/payment/${payment.id}`)}
                        />
                        <Button
                          size='sm'
                          colorScheme='green'
                          leftIcon={<MdCheckCircle />}
                          isLoading={validatingId === payment.id}
                          onClick={() => handleValidatePayment(payment)}
                        >
                          Validate
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Card>

        <Card p='22px' overflowX='auto'>
          <SectionHeader
            title='Recent Students'
            action={
              <Button size='sm' variant='outline' onClick={() => navigate('/admin/students')}>
                Student list
              </Button>
            }
          />
          <Table variant='simple' size='sm'>
            <Thead>
              <Tr>
                <Th borderColor={borderColor}>Student</Th>
                <Th borderColor={borderColor}>Field</Th>
                <Th borderColor={borderColor}>Applied</Th>
                <Th borderColor={borderColor}>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {students.length === 0 ? (
                <Tr>
                  <Td colSpan={4} borderColor='transparent'>
                    <Text color={mutedText} fontSize='sm'>No students found for this center.</Text>
                  </Td>
                </Tr>
              ) : (
                students.map((student) => (
                  <Tr key={student.id} _hover={{ bg: rowHoverBg }}>
                    <Td borderColor='transparent'>
                      <Text color={textColor} fontSize='sm' fontWeight='700'>{student.name}</Text>
                      <Text color={mutedText} fontSize='xs'>{student.matricule || student.email}</Text>
                    </Td>
                    <Td borderColor='transparent'>
                      <Text color={textColor} fontSize='sm'>{student.field || 'N/A'}</Text>
                    </Td>
                    <Td borderColor='transparent'>
                      <Text color={textColor} fontSize='sm'>{formatDate(student.application_date)}</Text>
                    </Td>
                    <Td borderColor='transparent'>
                      <IconButton
                        aria-label='View student'
                        icon={<MdVisibility />}
                        size='sm'
                        variant='ghost'
                        onClick={() => navigate(`/admin/student/${student.id}`)}
                      />
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Card>
      </SimpleGrid>

      <Card p='22px' overflowX='auto'>
        <SectionHeader
          title='Recent Tuition Payments'
          action={
            <Button size='sm' variant='outline' onClick={() => navigate('/admin/student-reports/tuition-payments?limit=100')}>
              Open report
            </Button>
          }
        />
        <Table variant='simple' size='sm'>
          <Thead>
            <Tr>
              <Th borderColor={borderColor}>Student</Th>
              <Th borderColor={borderColor}>Date</Th>
              <Th borderColor={borderColor}>Amount</Th>
              <Th borderColor={borderColor}>Status</Th>
              <Th borderColor={borderColor}>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {recentPayments.length === 0 ? (
              <Tr>
                <Td colSpan={5} borderColor='transparent'>
                  <Text color={mutedText} fontSize='sm'>No recent payments found.</Text>
                </Td>
              </Tr>
            ) : (
              recentPayments.map((payment) => (
                <Tr key={payment.id} _hover={{ bg: rowHoverBg }}>
                  <Td borderColor='transparent'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>{getPaymentStudentName(payment)}</Text>
                    <Text color={mutedText} fontSize='xs'>{payment.reference_number || getPaymentTuitionLabel(payment)}</Text>
                  </Td>
                  <Td borderColor='transparent'>
                    <Text color={textColor} fontSize='sm'>{formatDate(payment.payment_date)}</Text>
                  </Td>
                  <Td borderColor='transparent'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>{formatCurrency(payment.amount)}</Text>
                  </Td>
                  <Td borderColor='transparent'>
                    <StatusBadge status={payment.status} />
                  </Td>
                  <Td borderColor='transparent'>
                    <IconButton
                      aria-label='View payment'
                      icon={<MdVisibility />}
                      size='sm'
                      variant='ghost'
                      onClick={() => navigate(`/admin/payment/${payment.id}`)}
                    />
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Card>
    </Box>
  );
}

export default CenterAdminDashboard;
